import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { warrantiesInfo } from '../../../../../core/projects.selectors';
import { FetchWarrantiesRequest } from '../../../../../core/projects.actions';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../shared/services';
import { AppHttpService } from '../../../../../../../shared/services/app-http.service';
import { WarrantyPackageDetailComponent } from './warranty-package-detail.component';
import { WarrantyPackageFormComponent } from './warranty-package-form.component';
import { WarrantyClaimDetailComponent } from './warranty-claim-detail.component';
import { WarrantyClaimFormComponent } from './warranty-claim-form.component';
import { saveAs } from 'file-saver';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-warranty-packages-list',
  templateUrl: './warranty-packages-list.component.html',
  styleUrls: ['./warranty-packages-list.component.scss'],
  standalone: false,
})
export class WarrantyPackagesListComponent implements OnInit, OnDestroy {
  projectId: string;
  warranties: any[] = [];
  packages: any[] = [];
  totalRecords = 0;
  selectedRow: any = null;
  account: any;
  role: string;

  form: FormGroup;
  pagination = { start: 0, pageSize: 10 };
  selectedStatuses: string[] = [];
  sortByColumns = ['Package #', 'Title', 'Status', 'Sent Date', 'Due Date'];
  selectedSortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'desc';
  private readonly sortFieldMap: Record<string, string> = {
    'Package #': 'package_number',
    'Title': 'title',
    'Status': 'status',
    'Sent Date': 'sent_date',
    'Due Date': 'due_date',
  };

  statusList = [
    { value: 'draft', label: 'Draft', isChecked: false },
    { value: 'sent', label: 'Sent', isChecked: false },
    { value: 'responded', label: 'Responded', isChecked: false },
    { value: 'closed', label: 'Closed', isChecked: false },
  ];

  statusMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    draft: { label: 'Draft', bgColor: 'bg-draft', textColor: 'txt-draft' },
    sent: { label: 'Sent', bgColor: 'bg-sent', textColor: 'txt-sent' },
    in_progress: { label: 'In Progress', bgColor: 'bg-sent', textColor: 'txt-sent' },
    responded: { label: 'Responded', bgColor: 'bg-responded', textColor: 'txt-responded' },
    closed: { label: 'Closed', bgColor: 'bg-closed', textColor: 'txt-closed' },
  };

  severityMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
    low: { label: 'Low', bgColor: 'bg-sev-low', textColor: 'txt-sev-low' },
    medium: { label: 'Medium', bgColor: 'bg-sev-medium', textColor: 'txt-sev-medium' },
    high: { label: 'High', bgColor: 'bg-sev-high', textColor: 'txt-sev-high' },
    critical: { label: 'Critical', bgColor: 'bg-sev-critical', textColor: 'txt-sev-critical' },
  };

  claimStatusMap: Record<string, { label: string; icon: string; iconColor: string }> = {
    draft: { label: 'Draft', icon: '○', iconColor: '#8E8E93' },
    submitted: { label: 'Submitted', icon: '◌', iconColor: '#1036D6' },
    sent_to_contractor: { label: 'Sent to contractor', icon: '◌', iconColor: '#F7B500' },
    responded: { label: 'Responded', icon: '◉', iconColor: '#F7B500' },
    verified: { label: 'Verified', icon: '✓', iconColor: '#36AB19' },
    closed: { label: 'Closed', icon: '✓', iconColor: '#36AB19' },
    sent_back: { label: 'Sent back', icon: '✗', iconColor: '#E02020' },
    rejected: { label: 'Rejected', icon: '✗', iconColor: '#E02020' },
  };

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private authService: AuthService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private appHttpService: AppHttpService,
    private dialog: MatDialog,
  ) {
    this.form = new FormGroup({ searchText: new FormControl('') });
  }

  ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.account = this.authService.getCurrentUser();
    this.role = this.account?.roles?.find(r => r.project_id === this.projectId)?.role || '';

    this.store.dispatch(FetchWarrantiesRequest({ payload: { projectId: this.projectId } }));

    this.store.select(warrantiesInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(info => {
        if (info?.list?.length) {
          this.warranties = info.list;
          this.fetchPackages();
        }
      });

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        const next = qp.status
          ? (Array.isArray(qp.status) ? qp.status : [qp.status])
          : [];
        const changed =
          next.length !== this.selectedStatuses.length
          || next.some(s => !this.selectedStatuses.includes(s));
        if (changed) {
          this.selectedStatuses = next;
          this.pagination.start = 0;
          this.fetchPackages();
        }
      });

    this.form.get('searchText')?.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(() => {
        this.pagination.start = 0;
        this.fetchPackages();
      });

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => (this.selectedRow = null));

    // Listen for panel close to refresh data
    this.projectsService.closeRightPanel
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.selectedRow = null;
        this.fetchPackages();
      });
  }

  fetchPackages(): void {
    if (!this.projectId) return;
    const params: any = {
      start: this.pagination.start,
      total: this.pagination.pageSize,
    };
    if (this.selectedStatuses.length) params.status = this.selectedStatuses.join(',');
    const search = this.form.get('searchText')?.value?.trim();
    if (search) params.search = search;
    if (this.selectedSortColumn) {
      const field = this.sortFieldMap[this.selectedSortColumn];
      if (field) {
        params.sort_by = field;
        params.sort_order = this.sortDirection;
      }
    }

    this.appHttpService.getService(
      `/v1/warranty/project/${this.projectId}/claim-packages`,
      params,
    ).subscribe({
      next: (resp: any) => {
        const data = resp?.data || resp || [];
        this.packages = Array.isArray(data) ? data : (data.list || []);
        this.totalRecords = resp?.meta?.totalRecords || resp?.totalRecords || this.packages.length;
      },
      error: () => {
        this.packages = [];
        this.totalRecords = 0;
      },
    });
  }

  onPageChanged(page: number): void {
    this.pagination.start = (page - 1) * this.pagination.pageSize;
    this.fetchPackages();
  }

  clearSearch(): void {
    this.form.get('searchText')?.setValue('');
  }

  onSortColumnChange(event: any): void {
    this.selectedSortColumn = event?.value ?? null;
    this.sortDirection = 'desc';
    this.pagination.start = 0;
    this.fetchPackages();
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.fetchPackages();
  }

  removeSort(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedSortColumn = null;
    this.fetchPackages();
  }

  @ViewChild('sortSelect') sortSelect?: MatSelect;

  openSortDropdown(event?: Event): void {
    // Material renders the select panel and its backdrop as an inline popover
    // *inside* this wrapper, so the click that closes the panel bubbles back
    // here and would reopen it. Only react to clicks on the button itself.
    const target = event?.target as HTMLElement | null;
    if (target?.closest('.cdk-overlay-popover, .cdk-overlay-backdrop')) return;
    setTimeout(() => this.sortSelect?.open());
  }

  toggleExpand(pkg: any, event: Event): void {
    event.stopPropagation();
    pkg._expanded = !pkg._expanded;
    if (pkg._expanded && !pkg._claims) {
      this.loadPackageClaims(pkg);
    }
  }

  loadPackageClaims(pkg: any): void {
    pkg._claimsLoading = true;
    this.appHttpService.getService(`/v1/warranty/claim-packages/${pkg.uuid || pkg.id}`).subscribe({
      next: (resp: any) => {
        const detail = resp?.data || resp;
        pkg._claims = detail.claims || [];
        pkg._claimsLoading = false;
      },
      error: () => {
        pkg._claims = [];
        pkg._claimsLoading = false;
      },
    });
  }

  rowClicked(row: any): void {
    this.selectedRow = row;
    if (row.status === 'draft') {
      this.projectsService.openRightPanel.next({
        component: WarrantyPackageFormComponent,
        data: {
          warranties: this.warranties,
          projectId: this.projectId,
          editPackage: { ...row },
        },
      });
    } else {
      this.projectsService.openRightPanel.next({
        component: WarrantyPackageDetailComponent,
        data: {
          ...row,
          projectId: this.projectId,
          warranties: this.warranties,
        },
      });
    }
  }

  claimClicked(claim: any, event: Event): void {
    event.stopPropagation();
    if (['draft', 'sent_back'].includes(claim.status)) {
      this.projectsService.openRightPanel.next({
        component: WarrantyClaimFormComponent,
        data: {
          editClaim: { ...claim },
          warranties: this.warranties,
          projectId: this.projectId,
        },
      });
    } else {
      this.projectsService.openRightPanel.next({
        component: WarrantyClaimDetailComponent,
        data: { ...claim },
      });
    }
  }

  createPackage(): void {
    this.selectedRow = null;
    this.projectsService.openRightPanel.next({
      component: WarrantyPackageFormComponent,
      data: { warranties: this.warranties, projectId: this.projectId },
    });
  }

  isPM(): boolean {
    return this.role === 'project-manager' || this.role === 'org-admin';
  }

  deletePackage(pkg: any): void {
    const dialogData = new ConfirmDialogModel('Delete Package', 'Delete this draft package? This cannot be undone.', 'Delete', 'Cancel');
    this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: dialogData })
      .afterClosed().subscribe(result => {
        if (!result) return;
        this.appHttpService.deleteService(`/v1/warranty/claim-packages/${pkg.uuid || pkg.id}`).subscribe({
          next: () => this.fetchPackages(),
        });
      });
  }

  // --- Downloads ---

  downloadKmz(pkg: any): void {
    this.appHttpService.getService(
      `/v1/warranty/claim-packages/${pkg.uuid || pkg.id}/kmz`,
      {},
      { responseType: 'blob' },
    ).subscribe({
      next: (resp: Blob) => saveAs(resp, `${pkg.package_number}.kmz`),
    });
  }

  downloadCsv(pkg: any): void {
    this.appHttpService.getService(
      `/v1/warranty/claim-packages/${pkg.uuid || pkg.id}/csv`,
      {},
      { responseType: 'text' },
    ).subscribe({
      next: (csv: string) => {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${pkg.package_number}.csv`);
      },
    });
  }

  downloadPdf(pkg: any): void {
    this.appHttpService.getService(
      `/v1/warranty/claim-packages/${pkg.uuid || pkg.id}/pdf`,
      {},
      { responseType: 'text' },
    ).subscribe({
      next: (html: string) => {
        const w = window.open('', '_blank');
        if (w) { w.document.write(html); w.document.close(); }
      },
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
